import React from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { LogIn, ExternalLink, Facebook, Instagram, Linkedin, Youtube, Twitter, Globe, Phone } from 'lucide-react';

const Footer: React.FC = () => {
  const navigate = useNavigate();

  const socialLinks = [
    {
      name: "Facebook",
      url: "https://www.facebook.com/carolinatrophyroofs",
      icon: Facebook
    },
    {
      name: "Instagram", 
      url: "https://www.instagram.com/carolinatrophyroofs/?hl=en",
      icon: Instagram
    },
    {
      name: "LinkedIn",
      url: "https://www.linkedin.com/company/81146474/admin/dashboard/",
      icon: Linkedin
    },
    {
      name: "YouTube",
      url: "https://www.youtube.com/channel/UC8pyyQN0WcMTbT-OHTQbhkA",
      icon: Youtube
    },
    {
      name: "TikTok",
      url: "https://www.tiktok.com/@carolinatrophyroofs",
      icon: Globe
    },
    {
      name: "X (Twitter)",
      url: "https://x.com/RoofsTrophy",
      icon: Twitter
    },
    {
      name: "BBB",
      url: "https://www.bbb.org/us/nc/rocky-mount/profile/roofing-contractors/carolina-trophy-roofs-llc-0593-90328252",
      icon: Globe
    },
    {
      name: "Yelp",
      url: "https://www.yelp.com/biz/carolina-trophy-roofs-rocky-mount",
      icon: Globe
    },
    {
      name: "Trustpilot",
      url: "https://www.trustpilot.com/review/carolinatrophyroofs.com",
      icon: Globe
    },
    {
      name: "Angi Reviews",
      url: "https://www.angi.com/companylist/us/nc/rocky-mount/carolina-trophy-roofs-reviews-10015537.htm",
      icon: Globe
    },
    {
      name: "Pinterest",
      url: "https://www.pinterest.com/carolinatrophyroofs/",
      icon: Globe
    },
    {
      name: "National Roofing Directory",
      url: "https://www.nationalroofingdirectory.org/united-states/rocky-mount/roofing-1/carolina-trophy-roofs",
      icon: Globe
    },
    {
      name: "Thumbtack",
      url: "https://www.thumbtack.com/nc/rocky-mount/roofing/carolina-trophy-roofs-votedbest-roofer-near-me/service/405902292554686466",
      icon: Globe
    },
    {
      name: "TripAdvisor",
      url: "https://www.tripadvisor.com/Attraction_Review-g49487-d26260572-Reviews-Carolina_Trophy_Roofs-Rocky_Mount_North_Carolina.html",
      icon: Globe
    },
    {
      name: "MapQuest",
      url: "https://www.mapquest.com/us/north-carolina/carolina-trophy-roofs-427666756",
      icon: Globe
    },
    {
      name: "Linktree",
      url: "https://linktr.ee/CarolinaTrophyRoofs",
      icon: Globe
    }
  ];

  const quickLinks = [
    { name: "Services", path: "/services" },
    { name: "Locations", path: "/locations" },
    { name: "Brands", path: "/brands" },
    { name: "Resources", path: "/resources" },
    { name: "About Us", path: "/about" },
    { name: "Contact", path: "/contact" },
    { name: "Blog", path: "/blog" },
    { name: "Gallery", path: "/gallery" }
  ];

  const services = [
    { name: "Residential Roofing", path: "/residential-roofing" },
    { name: "Commercial Roofing", path: "/commercial-roofing" },
    { name: "Storm Damage", path: "/storm-damage" },
    { name: "Church Steeple Repair", path: "/specialty-services/church-steeple-repair" },
    { name: "Roof Coating", path: "/specialty-services/roof-coating" },
    { name: "Emergency Repairs", path: "/storm-damage" }
  ];

  const topLocations = [
    { name: "Rocky Mount", path: "/locations/rocky-mount" },
    { name: "Greenville", path: "/locations/greenville" },
    { name: "Wilson", path: "/locations/wilson" },
    { name: "Raleigh", path: "/locations/raleigh" },
    { name: "Durham", path: "/locations/durham" },
    { name: "Tarboro", path: "/locations/tarboro" },
    { name: "View All Locations", path: "/service-areas" }
  ];

  const resources = [
    { name: "General Roofing FAQ", path: "/resources/general-roofing-faq" },
    { name: "Insurance Claims", path: "/resources/insurance-claim-process" },
    { name: "Roofing Materials", path: "/resources/understanding-roofing-materials" },
    { name: "Roof Replacement Process", path: "/resources/roof-replacement-process" },
    { name: "Roof Ventilation Guide", path: "/resources/roof-ventilation-guide" },
    { name: "Working with Adjusters", path: "/resources/working-with-adjusters" },
    { name: "Free Consultation", path: "/book-inspection" }
  ];

  return (
    <footer className="bg-black text-white py-12 mt-auto">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          
          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-brand-trophy-gold">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button 
                    onClick={() => navigate(link.path)}
                    className="text-gray-300 hover:text-brand-trophy-gold transition-colors text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-brand-trophy-gold">Our Services</h3>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index}>
                  <button 
                    onClick={() => navigate(service.path)}
                    className="text-gray-300 hover:text-brand-trophy-gold transition-colors text-sm"
                  >
                    {service.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Top Locations */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-brand-trophy-gold">Service Areas</h3>
            <ul className="space-y-2">
              {topLocations.map((location, index) => (
                <li key={index}>
                  <button 
                    onClick={() => navigate(location.path)}
                    className="text-gray-300 hover:text-brand-trophy-gold transition-colors text-sm"
                  >
                    {location.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-brand-trophy-gold">Resources</h3>
            <ul className="space-y-2">
              {resources.map((resource, index) => (
                <li key={index}>
                  <button 
                    onClick={() => navigate(resource.path)}
                    className="text-gray-300 hover:text-brand-trophy-gold transition-colors text-sm"
                  >
                    {resource.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="border-t border-gray-800 pt-8">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold mb-4 text-brand-trophy-gold">Connect With Us</h3>
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 max-w-4xl mx-auto">
              {socialLinks.map((link, index) => {
                const IconComponent = link.icon;
                return (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center gap-1 text-xs text-gray-300 hover:text-brand-trophy-gold transition-colors py-2 px-2 rounded hover:bg-gray-800 group"
                    title={link.name}
                  >
                    <IconComponent className="h-6 w-6 group-hover:scale-110 transition-transform" />
                    <span className="text-[10px] leading-tight text-center">{link.name}</span>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Phone Number Button */}
          <div className="text-center mb-6">
            <Button
              onClick={() => window.open('tel:+12523022003', '_self')}
              className="px-6 py-3 rounded-xl font-bold text-sm tracking-wide transition-all duration-300 hover:scale-105 border-2 border-gray-700 group shadow-lg shadow-black/30 bg-green-500 hover:bg-green-600 text-white"
              style={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
              }}
              aria-label="Call Carolina Trophy Roofs at (252) 302-2003"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call (252) 302-2003
            </Button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-300">
              © 2024 Carolina Trophy Roofs. All rights reserved.
            </p>
            <div className="mt-2 flex justify-center gap-4 text-xs text-gray-400">
              <a href="/privacy" className="hover:text-brand-trophy-gold transition-colors">
                Privacy Policy
              </a>
              <span>•</span>
              <a href="/sms-terms" className="hover:text-brand-trophy-gold transition-colors">
                SMS Terms
              </a>
              <span>•</span>
              <a href="/sitemap" className="hover:text-brand-trophy-gold transition-colors">
                Sitemap
              </a>
              <span>•</span>
              <a href="/sitemap.xml" className="hover:text-brand-trophy-gold transition-colors">
                XML Sitemap
              </a>
            </div>
          </div>

          {/* Powered by BrockStar Media Corp */}
          <div className="text-center pt-4 border-t border-gray-600 w-full max-w-xs">
            <a 
              href="https://BrockStarMedia.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-gray-400 hover:text-brand-trophy-gold transition-colors"
            >
              <span className="text-xs">Powered by</span>
              <img 
                src="/lovable-uploads/3baf56ef-07c9-431d-9cca-4338eb9585ed.png" 
                alt="BrockStar Media Corp" 
                className="h-12 w-auto rounded-lg"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
